import type { AxiosRequestConfig } from 'axios'
import { request } from '@/utils/http'
import { isMdmImportFileTooLarge } from '@/utils/mdm-import'

/** MDM 扩展查询失败时不弹全局 404（页面有兜底数据） */
const mdmQuerySilentConfig: AxiosRequestConfig & { showErrorMessage: false } = {
  showErrorMessage: false
}

export interface EnterpriseMdmAttributeVO {
  mdmId?: string
  counterpartyCode?: string
  counterpartyNature?: string
  mainCurrency?: string
  cooperatingSubsidiary?: string
  isInternalEntity?: boolean
  counterpartyType?: string
  partnerTypes?: string[]
  attributes?: Record<string, unknown>
}

export interface EnterpriseReceivingAddressVO {
  id?: string
  isPrimary?: boolean
  receivingCountry?: string
  receivingProvince?: string
  receivingCity?: string
  receivingAddress?: string
  receivingCompanyName?: string
  receivingContactName?: string
  receivingContactPhone?: string
  courierName?: string
  carrierContact?: string
  vatRegistrationNumber?: string
}

/** 收货地址服务端分页结果，复用后端现有 Page 契约。 */
export interface EnterpriseReceivingAddressPageVO {
  list?: EnterpriseReceivingAddressVO[]
  records?: EnterpriseReceivingAddressVO[]
  total: number
  pageNum?: number
  pageSize?: number
}

export interface MdmSyncLogVO {
  receivedAt?: string
  operationType?: string
  mdmCode?: string
  success?: boolean
  errorMessage?: string
}

export interface MdmEnterpriseExtensionVO {
  shortName?: string
  legalRepresentative?: string
  mdmAttribute?: EnterpriseMdmAttributeVO | null
  receivingAddresses?: EnterpriseReceivingAddressVO[]
  mdmSyncLogs?: MdmSyncLogVO[]
}

export interface MdmCustomerVendorImportFailDetail {
  rowNum?: number
  fieldName?: string
  errorReason?: string
}

export interface MdmEnterpriseBankAccountVO {
  externalSource?: string
  action?: 'ADD' | 'UPDATE' | 'DELETE'
  id?: string
  currency?: string
  bankName?: string
  bankAccount?: string
  accountName?: string
  swiftCode?: string
  bankCountry?: string
  bankAddress?: string
}

export interface MdmEnterpriseInvoiceVO {
  externalSource?: string
  upstreamRemoved?: boolean
  action?: 'ADD' | 'UPDATE' | 'DELETE'
  id?: string
  invoiceCompanyName?: string
  invoiceTaxNo?: string
  invoiceAddress?: string
  invoicePhone?: string
  bankAccounts?: MdmEnterpriseBankAccountVO[]
}

export interface MdmEnterpriseFinancialVO {
  invoiceInfoList?: MdmEnterpriseInvoiceVO[]
  reconciliationContactId?: string
}

export interface MdmContactAddressVO {
  externalSource?: string
  id?: string
  isShippingAddress?: boolean
  country?: string
  province?: string
  city?: string
  detailAddress?: string
}

export interface MdmContactAddressRequest {
  isShippingAddress?: boolean
  country?: string
  province?: string
  city?: string
  detailAddress?: string
}

export interface MdmEnterpriseContactExtraVO {
  addressesByContactId?: Record<string, MdmContactAddressVO[]>
  jobTitleByContactId?: Record<string, string>
}

export interface MdmContactWithAddressesSaveRequest {
  contactId: string
  jobTitle?: string
  contactAddresses?: MdmContactAddressRequest[]
}

export interface MdmEnterpriseFinancialSaveRequest {
  reconciliationSelectionProvided?: boolean
  reconciliationContactKey?: string
  invoiceInfoList?: Array<{
    id?: string
    action?: 'ADD' | 'UPDATE' | 'DELETE'
    invoiceCompanyName?: string
    invoiceTaxNo?: string
    invoiceAddress?: string
    invoicePhone?: string
    bankAccounts?: MdmEnterpriseBankAccountVO[]
  }>
  reconciliationContactId?: string
}

/** 专用提交不包含主档写入；来源只由后端赋值。 */
export interface MdmEnterpriseDetailsSaveRequest {
  contacts?: Array<import('./enterprise').EnterpriseContactRequest & {
    clientKey?: string
    defaultSelection?: boolean
    reconciliationSelection?: boolean
    jobTitle?: string
    contactAddresses?: MdmContactAddressRequest[]
  }>
  financial?: MdmEnterpriseFinancialSaveRequest
}

export interface MdmCustomerVendorImportResultVO {
  successCount: number
  failCount: number
  failDetails?: MdmCustomerVendorImportFailDetail[]
}

/** 兼容 Result 包装及字段缺失 */
export function normalizeMdmImportResult(res: unknown): MdmCustomerVendorImportResultVO {
  const body =
    res !== null && res !== undefined && typeof res === 'object' && 'content' in (res as object)
      ? (res as { content?: MdmCustomerVendorImportResultVO }).content
      : (res as MdmCustomerVendorImportResultVO)
  return {
    successCount: Number(body?.successCount ?? 0),
    failCount: Number(body?.failCount ?? 0),
    failDetails: body?.failDetails ?? []
  }
}

/** 下载 MDM 客商 Excel 导入模板 */
export function downloadMdmImportTemplate() {
  return request.get<Blob>(
    '/enterprise/tenant/mdm/customer-vendor/import-template',
    undefined,
    { responseType: 'blob' }
  )
}

/** 查询企业 MDM 扩展信息 */
export function fetchMdmEnterpriseExtension(enterpriseId: string) {
  return request.get<MdmEnterpriseExtensionVO>(
    `/enterprise/tenant/mdm/enterprise/${enterpriseId}/extension`,
    { includeReceivingAddresses: false },
    mdmQuerySilentConfig
  )
}

/** 仅查询当前页收货地址，不加载全量地址，不修改任何明细数据。 */
export function fetchMdmReceivingAddressPage(enterpriseId: string, pageNum: number, pageSize: number) {
  return request.get<EnterpriseReceivingAddressPageVO>(
    `/enterprise/tenant/mdm/enterprise/${enterpriseId}/receiving-addresses/page`,
    { pageNum, pageSize },
    mdmQuerySilentConfig
  )
}

/** MDM 客商 Excel 批量导入 */
export function importMdmCustomerVendor(file: File) {
  if (isMdmImportFileTooLarge(file)) {
    return Promise.reject(Object.assign(new Error('MDM_IMPORT_FILE_TOO_LARGE'), {
      code: 'MDM_IMPORT_FILE_TOO_LARGE'
    }))
  }
  const formData = new FormData()
  formData.append('file', file)
  return request
    .post<MdmCustomerVendorImportResultVO>(
      '/enterprise/tenant/mdm/customer-vendor/import',
      formData
    )
    .then(normalizeMdmImportResult)
}

/** 导出 MDM 客商导入失败明细 Excel */
export function exportMdmImportFailExcel(failDetails: MdmCustomerVendorImportFailDetail[]) {
  return request.post<Blob>(
    '/enterprise/tenant/mdm/customer-vendor/import-fail-export',
    failDetails,
    { responseType: 'blob' }
  )
}

/** 查询企业财务信息（多开票主子表） */
export function fetchMdmEnterpriseFinancial(enterpriseId: string) {
  return request.get<MdmEnterpriseFinancialVO>(
    `/enterprise/tenant/mdm/enterprise/${enterpriseId}/financial`,
    undefined,
    mdmQuerySilentConfig
  )
}

/** 保存企业财务信息（多开票主子表） */
export function saveMdmEnterpriseFinancial(
  enterpriseId: string,
  data: MdmEnterpriseFinancialSaveRequest
) {
  return request.post<void>(
    `/enterprise/tenant/mdm/enterprise/${enterpriseId}/financial`,
    data
  )
}

/** 查询企业联系人 MDM 扩展（地址 + 岗位） */
export function fetchMdmContactAddresses(enterpriseId: string) {
  return request.get<MdmEnterpriseContactExtraVO>(
    `/enterprise/tenant/mdm/enterprise/${enterpriseId}/contact-addresses`,
    undefined,
    mdmQuerySilentConfig
  )
}

/** 平台端：MDM 同步日志分页（control-tower 暂无页面，供运营后台或其它端接入） */
export function pageMdmSyncLogPlatform(data: {
  pageNum?: number
  pageSize?: number
  mdmCode?: string
  enterpriseId?: string
  success?: boolean
  receivedAtStart?: string
  receivedAtEnd?: string
}) {
  return request.post<{
    list?: MdmSyncLogVO[]
    records?: MdmSyncLogVO[]
    total?: number
    pageNum?: number
    pageSize?: number
  }>('/enterprise/platform/mdm/sync-log/page', data)
}

/** 批量保存联系人地址 */
export function saveMdmContactAddresses(
  enterpriseId: string,
  items: MdmContactWithAddressesSaveRequest[]
) {
  return request.post<void>(
    `/enterprise/tenant/mdm/enterprise/${enterpriseId}/contact-addresses`,
    items
  )
}
